import { forwardRef } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
type AuthModalFieldHelperTone = 'success' | 'destructive' | 'muted'

interface AuthModalFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
	className?: string
	error?: string
	helperText?: string
	helperTone?: AuthModalFieldHelperTone
	isChecking?: boolean
}

const helperToneClasses: Record<AuthModalFieldHelperTone, string> = {
	success: 'text-success',
	destructive: 'text-destructive',
	muted: 'text-muted-foreground',
}

export const AuthModalField = forwardRef<HTMLInputElement, AuthModalFieldProps>(function AuthModalField(
	{ className, error, helperText, helperTone = 'muted', isChecking = false, id, ...props },
	ref,
) {
	const fieldId = id ?? props.name
	const feedbackId = fieldId ? `${fieldId}-feedback` : undefined
	const showHelper = !error && (isChecking || Boolean(helperText))

	return (
		<div className='space-y-1.5'>
			<Input
				ref={ref}
				id={fieldId}
				error={Boolean(error)}
				aria-invalid={Boolean(error)}
				aria-describedby={error ? `${fieldId}-error` : showHelper ? feedbackId : undefined}
				{...props}
			/>
			{error ? (
				<p id={`${fieldId}-error`} className='text-xs leading-[140%] tracking-[-0.18px] text-destructive'>
					{error}
				</p>
			) : null}
			{!error && isChecking ? (
				<div id={feedbackId} className='flex items-center gap-1.5 text-xs leading-[140%] tracking-[-0.18px] text-muted-foreground'>
					<Loader2 className='animate-spin h-3.5 w-3.5' aria-hidden />
					<span>Checking availability...</span>
				</div>
			) : null}
			{!error && !isChecking && helperText ? (
				<p id={feedbackId} className={cn('text-xs leading-[140%] tracking-[-0.18px]', helperToneClasses[helperTone])}>
					{helperText}
				</p>
			) : null}
		</div>
	)
})
